// Theme resolution for annotations. Geometry never stores raw colors — the
// `style` token is mapped to a color here so the same annotation can be re-themed
// later. Counters carry a palette slot as `counter:<index>` so each group gets a
// distinct, stable color.

export const COUNTER_PALETTE = [
  '#34d399', // emerald
  '#60a5fa', // blue
  '#f472b6', // pink
  '#fb923c', // orange
  '#a78bfa', // violet
  '#22d3ee', // cyan
  '#4ade80', // green
  '#f87171', // red
];

const MEASUREMENT = '#fbbf24'; // amber — distinct from every counter color
const ARROW = '#38bdf8'; // sky — distinct from measurements and counters
const RECTANGLE = '#a78bfa'; // violet
const ELLIPSE = '#2dd4bf'; // teal
const POLYGON = '#e879f9'; // fuchsia — distinct from violet/teal/pink

/** Palette slot encoded in a `counter:<index>` style token (0 if absent). */
export function counterColorIndex(style: string | undefined): number {
  const m = style ? /^counter:(\d+)$/.exec(style) : null;
  return m ? Number(m[1]) : 0;
}

export function counterColor(style: string | undefined): string {
  return COUNTER_PALETTE[counterColorIndex(style) % COUNTER_PALETTE.length];
}

export function colorForStyle(style: string | undefined): string {
  if (style && style.startsWith('counter')) return counterColor(style);
  if (style === 'arrow') return ARROW;
  if (style === 'rectangle') return RECTANGLE;
  if (style === 'ellipse') return ELLIPSE;
  if (style && style.startsWith('polygon')) return POLYGON;
  return MEASUREMENT;
}

/** Whether a polygon's style token asks for a smoothed (spline) outline.
 *  Encoded in the token (`polygon:smooth` vs `polygon`) so no schema change is
 *  needed — mirrors how counters carry their palette slot. */
export function isSmoothPolygon(style: string | undefined): boolean {
  return style === 'polygon:smooth';
}

/** The style token for a new polygon of the given mode. */
export function polygonStyle(smooth: boolean): string {
  return smooth ? 'polygon:smooth' : 'polygon';
}

/** Arrow label: the name if set, otherwise the default "Arrow". */
export function arrowLabel(text: string | undefined): string {
  return text && text.trim() ? text.trim() : 'Arrow';
}

// Cached number formatters (0/1/2 decimals) with thousands separators. Cached
// because label formatters run on the per-frame overlay render during pan/zoom.
const GROUP_FMT = [0, 1, 2].map(
  (d) => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }),
);
/** Format a number with thousands separators and a fixed number of decimals. */
function group(n: number, decimals: 0 | 1 | 2 = 0): string {
  return GROUP_FMT[decimals].format(n);
}

/** Area from image-pixel² and the slide's mm/px (px²/Mpx² when synthetic). */
export function formatArea(imgAreaPx2: number, mmPerPx: number | null): string {
  if (mmPerPx == null) {
    if (imgAreaPx2 >= 1e6) return `${group(imgAreaPx2 / 1e6, 2)} Mpx²`;
    return `${group(imgAreaPx2)} px²`;
  }
  const mm2 = imgAreaPx2 * mmPerPx * mmPerPx;
  // A specimen on an X-ray is centimetres across, as a length already reads.
  if (mm2 >= 100) return `${group(mm2 / 100, 2)} cm²`;
  if (mm2 >= 1) return `${group(mm2, 2)} mm²`;
  return `${group(mm2 * 1e6)} µm²`;
}

/** A measure length from its image-pixel distance and the slide's mm/px (or px when
 *  the spacing is unknown/synthetic). */
export function formatLength(imgDistPx: number, mmPerPx: number | null): string {
  if (mmPerPx == null) return `${group(imgDistPx)} px`;
  const mm = imgDistPx * mmPerPx;
  if (mm >= 10) return `${group(mm / 10, 2)} cm`;
  if (mm >= 1) return `${group(mm, 2)} mm`;
  return `${group(mm * 1000, 1)} µm`;
}

/** Counter label: "<name>: <count>", defaulting the name to "Counter". */
export function counterLabel(text: string | undefined, count: number): string {
  const name = text && text.trim() ? text.trim() : 'Counter';
  return `${name}: ${group(count)}`;
}

/** Measure label: the measurement, prefixed with "<name>: " when a name is set. */
export function measureLabel(text: string | undefined, measurement: string): string {
  return text && text.trim() ? `${text.trim()}: ${measurement}` : measurement;
}
